/**
 * Skeleton-first SRP -> job page navigation.
 *
 * Vike's client router fetches the next page's pageContext from the server before it renders
 * anything (always, in this app: see utils/navigation/page-context-prefetch.ts for why), so a
 * card click used to leave the SRP frozen under the view transition's old snapshot for the whole
 * round trip. Instead, the card registers what it already knows (title, company, logo, brand
 * colour) on click; +onPageTransitionStart mounts JobPreviewShell over the page straight away
 * (inside the card-morph view transition, see job-card-transition.ts), and the real job page
 * renders underneath once its data arrives. JobRouteContent (LayoutDefault.tsx) dismisses the
 * shell from a layout effect as it commits, so the swap happens in the same frame the real page
 * first paints. Vike's own flow (URL, history, hooks, scroll) is untouched; the shell is purely an
 * overlay with its own React root.
 */
import React from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { flushSync } from 'react-dom';
import { JobPreviewShell, type JobPreviewSeed } from '../../components/job-preview/JobPreviewShell';

export type { JobPreviewSeed };

/** html class while the shell is up: job-card-transition.css drops the SRP card's morph names. */
export const JOB_PREVIEW_OPEN_CLASS = 'jc-preview-open';
/** A registered card click is only used by a navigation that starts this soon after it. */
const SEED_MAX_AGE_MS = 5000;
/** Never leave the overlay up if the navigation dies without reaching either dismissal path. */
const SAFETY_TIMEOUT_MS = 15_000;

const JOB_ID_IN_PATH_RE = /^\/(?:(?:en|fi|sv)\/)?(?:job|tyopaikka|jobb)\/([^/]+)\/[^/]+/i;

let pendingSeed: { seed: JobPreviewSeed; at: number } | null = null;
/**
 * A shell the card morph has promised to mount from its update callback. The browser can run that
 * callback late (it waits for a frame to capture the old snapshot, and slow devices or long tasks
 * delay that), late enough for the real job page to have rendered already. Dismissing before the
 * mount cancels the reservation, so a late callback never puts a skeleton over the real page.
 */
let reservation: object | null = null;
let shell: {
  root: Root;
  host: HTMLElement;
  seed: JobPreviewSeed;
  topOffset: number;
  /** The morph's `finished`, when the shell was mounted inside one: removal waits for it. */
  transitionFinished: Promise<unknown> | null;
  safetyTimeoutId: ReturnType<typeof setTimeout>;
  dismissRequested: boolean;
} | null = null;

export function jobIdFromPathname(pathname: string | undefined | null): string | null {
  const match = String(pathname ?? '').match(JOB_ID_IN_PATH_RE);
  return match ? decodeURIComponent(match[1]) : null;
}

let fallbackDismissTimeoutId: ReturnType<typeof setTimeout> | null = null;

function renderShell(root: Root, seed: JobPreviewSeed, topOffset: number): void {
  root.render(React.createElement(JobPreviewShell, { seed, topOffset }));
}

/**
 * History placeholder. Vike only changes the URL when it renders the next page, so with the shell
 * up the screen already looks like the job page while history still sits on the SRP: a Back
 * pressed during the load would skip the SRP and leave for whatever came before it. The card click
 * therefore pushes a copy of the SRP's own URL (a same-URL entry, so nothing on the SRP that reads
 * the URL, e.g. SearchContext, sees a change) and Link.tsx tells Vike to overwrite that entry when
 * the job page renders (consumeHistoryPlaceholderFor). Back during the load just pops the
 * placeholder: Vike cancels the in-flight render, and the popstate listener below drops the shell.
 * Once the job page renders, history is exactly SRP -> job page, as before.
 */
let placeholder: { href: string; at: number } | null = null;
let popstateListenerInstalled = false;

function samePathAndSearch(a: string, b: string): boolean {
  try {
    const x = new URL(a, window.location.href);
    const y = new URL(b, window.location.href);
    return x.origin === y.origin && x.pathname === y.pathname && x.search === y.search;
  } catch {
    return false;
  }
}

function installPopstateListener(): void {
  if (popstateListenerInstalled) return;
  popstateListenerInstalled = true;
  window.addEventListener('popstate', () => {
    placeholder = null;
    pendingSeed = null;
    dismissJobPreviewShell();
  });
}

/**
 * Link.tsx asks this right before navigate(): true means the click pushed a placeholder entry for
 * this exact href, so the navigation should overwrite it (Vike's overwriteLastHistoryEntry).
 */
export function consumeHistoryPlaceholderFor(href: string): boolean {
  if (typeof window === 'undefined' || !placeholder) return false;
  if (Date.now() - placeholder.at > SEED_MAX_AGE_MS || !samePathAndSearch(placeholder.href, href)) return false;
  return true;
}

/** +onPageTransitionEnd: a render landed, so whatever placeholder there was has been overwritten. */
export function settleHistoryPlaceholder(): void {
  placeholder = null;
}

/** Called from the SRP card's click handler, before Link.tsx calls navigate(). */
export function registerJobPreviewSeed(seed: JobPreviewSeed): void {
  pendingSeed = { seed, at: Date.now() };
  installPopstateListener();
  try {
    // One placeholder per pending navigation: a second card clicked mid-load reuses it.
    // null state: Vike's history patch stamps it as a user entry with a fresh timestamp, so a Back
    // onto the SRP entry counts as backward and Vike leaves the (same) SRP page alone.
    if (!placeholder) window.history.pushState(null, '', window.location.href);
    placeholder = { href: seed.href, at: Date.now() };
  } catch {
    placeholder = null;
  }
  // A second card clicked while the first job is still loading: Vike folds it into the navigation
  // already in flight without calling +onPageTransitionStart again, so this seed would never be
  // taken. Show the newly clicked job in the open shell instead of the first one.
  const current = shell;
  if (current && !current.dismissRequested && current.seed.jobId !== seed.jobId) {
    current.seed = seed;
    flushSync(() => renderShell(current.root, seed, current.topOffset));
  }
}

/** Take (and clear) the registered seed if it belongs to the job page this navigation goes to. */
export function takeJobPreviewSeedFor(urlPathname: string | undefined | null): JobPreviewSeed | null {
  const entry = pendingSeed;
  pendingSeed = null;
  if (!entry || Date.now() - entry.at > SEED_MAX_AGE_MS) return null;
  const jobId = jobIdFromPathname(urlPathname);
  return jobId && jobId === entry.seed.jobId ? entry.seed : null;
}

export function isJobPreviewShellOpen(): boolean {
  return shell !== null;
}

/**
 * Where the job page's content starts: the bottom of the job page's own header, which is not the
 * header on screen when the shell mounts (the SRP's mobile header is 37px, the job page's 59px),
 * so the shell can't just measure what's there. The job page records its header bottom each time
 * it renders (rememberJobPageHeaderBottom, per breakpoint, kept across visits); until it has, these
 * measured defaults apply (MinimalChrome's NavigationNavbar, 2026-10-04).
 */
const JOB_PAGE_HEADER_STORAGE_KEY = 'jc-job-page-header-bottom';
const DEFAULT_JOB_PAGE_HEADER_BOTTOM = { lg: 93, base: 59 } as const;

function headerBucket(): 'lg' | 'base' {
  return window.matchMedia?.('(min-width: 1024px)').matches ? 'lg' : 'base';
}

function readHeaderBottoms(): Partial<Record<'lg' | 'base', number>> {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(JOB_PAGE_HEADER_STORAGE_KEY) ?? '{}') as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as Partial<Record<'lg' | 'base', number>>) : {};
  } catch {
    return {};
  }
}

function jobPageHeaderBottom(): number {
  const bucket = headerBucket();
  const stored = readHeaderBottoms()[bucket];
  return typeof stored === 'number' && stored > 0 && stored < 400 ? stored : DEFAULT_JOB_PAGE_HEADER_BOTTOM[bucket];
}

/** Called by the job page (JobRouteContent) once it has rendered; see jobPageHeaderBottom. */
export function rememberJobPageHeaderBottom(): void {
  if (typeof window === 'undefined') return;
  const header = document.querySelector('header[role="banner"]');
  if (!header) return;
  const bottom = Math.round(header.getBoundingClientRect().bottom);
  if (bottom <= 0) return; // scrolled out of view or not laid out: nothing to learn
  try {
    const all = readHeaderBottoms();
    const bucket = headerBucket();
    if (all[bucket] === bottom) return;
    window.localStorage.setItem(JOB_PAGE_HEADER_STORAGE_KEY, JSON.stringify({ ...all, [bucket]: bottom }));
  } catch {
    /* storage unavailable: the defaults keep applying */
  }
}

/** Reserve a mount for a transition update callback; pass the token to mountJobPreviewShell. */
export function reserveJobPreviewShell(): object {
  reservation = {};
  return reservation;
}

/**
 * Mount synchronously (flushSync), so it's in the DOM when a view transition takes its new
 * snapshot. Pass the morph's `finished` when mounting inside one: removal then waits for it, since
 * removing the shell mid-animation would blank the morphing elements.
 */
export function mountJobPreviewShell(
  seed: JobPreviewSeed,
  transitionFinished: Promise<unknown> | null = null,
  reservationToken?: object,
): void {
  if (typeof document === 'undefined') return;
  if (reservationToken !== undefined) {
    if (reservationToken !== reservation) return; // dismissed (real page already up) before this ran
    reservation = null;
  }
  removeJobPreviewShellNow();
  if (fallbackDismissTimeoutId) {
    clearTimeout(fallbackDismissTimeoutId); // left over from an earlier navigation; not ours
    fallbackDismissTimeoutId = null;
  }
  const host = document.createElement('div');
  host.id = 'jc-job-preview-shell';
  document.body.appendChild(host);
  const topOffset = jobPageHeaderBottom();
  // Registered before rendering, so a render failure still goes through the normal removal path.
  shell = {
    // React 19 reports render errors here rather than throwing them out of flushSync.
    root: createRoot(host, {
      onUncaughtError: (error) => {
        console.warn('[job-preview-shell] render failed, removing the shell', error);
        removeJobPreviewShellNow();
      },
    }),
    host,
    seed,
    topOffset,
    transitionFinished,
    safetyTimeoutId: setTimeout(removeJobPreviewShellNow, SAFETY_TIMEOUT_MS),
    dismissRequested: false,
  };
  document.documentElement.classList.add(JOB_PREVIEW_OPEN_CLASS);
  try {
    const root = shell.root;
    flushSync(() => renderShell(root, seed, topOffset));
  } catch {
    // The shell is a nicety: on any failure, get out of the way and let the real page load.
    removeJobPreviewShellNow();
  }
}

/**
 * +onPageTransitionEnd's fallback for the job page: normally JobRouteContent's layout effect has
 * already removed the shell; this only covers that effect never running. Cleared by the next
 * mount so it can't remove a later navigation's shell.
 */
export function scheduleJobPreviewShellFallbackDismiss(delayMs: number): void {
  if (fallbackDismissTimeoutId) clearTimeout(fallbackDismissTimeoutId);
  fallbackDismissTimeoutId = setTimeout(() => {
    fallbackDismissTimeoutId = null;
    dismissJobPreviewShell();
  }, delayMs);
}

function removeJobPreviewShellNow(): void {
  const current = shell;
  if (!current) return;
  shell = null;
  clearTimeout(current.safetyTimeoutId);
  // Detach now so the very next paint shows the real page; unmount the shell's root on the next
  // tick, because this can run from JobRouteContent's layout effect, i.e. inside another root's
  // commit, where a synchronous root.unmount() is not allowed.
  current.host.remove();
  document.documentElement.classList.remove(JOB_PREVIEW_OPEN_CLASS);
  setTimeout(() => current.root.unmount(), 0);
}

/**
 * Remove the shell: right away, or as soon as the card morph it was mounted in has finished (the
 * real page is already rendered underneath by then, at the same positions).
 */
export function dismissJobPreviewShell(): void {
  reservation = null;
  const current = shell;
  if (!current || current.dismissRequested) return;
  current.dismissRequested = true;
  if (!current.transitionFinished) {
    removeJobPreviewShellNow();
    return;
  }
  const remove = () => {
    if (shell === current) removeJobPreviewShellNow();
  };
  current.transitionFinished.then(remove, remove);
}
